import { useState } from "react"

import { Dialog } from "@minuthq/meatball-ui-react"
import { useFormik } from "formik"

import { TaskForm } from "src/components/TaskManagement/TaskForm"
import { usePostTaskCreated } from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePostTask } from "src/data/taskManagement/taskManagementQueries"
import {
  TTaskPostBody,
  TTaskType,
} from "src/data/taskManagement/taskManagementTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { DiscardChangesDialog } from "src/ui/Dialog/DiscardChangesDialog"

const INITIAL_VALUE: TTaskPostBody = {
  title: "",
  home_id: "",
  category: "" as TTaskType,
  description: "",
  status: "todo",
  assigned_to: { type: "unassigned" },
  time_window: {
    due_date: "",
    earliest_start_date: undefined,
  },
}

const CREATE_TASK_FORM_ID = "create_task_form"

export function CreateTaskDialog({ onClose }: { onClose: () => void }) {
  const { t, langKeys } = useTranslate()
  const { orgId } = useOrganization()

  const [showConfirm, setShowConfirm] = useState(false)

  const postTask = usePostTask()
  const postTaskCreated = usePostTaskCreated()

  const form = useFormik<typeof INITIAL_VALUE>({
    initialValues: INITIAL_VALUE,
    onSubmit: (values) => {
      postTask.mutate(
        {
          path: { organization_id: orgId },
          body: { ...values },
        },
        {
          onSuccess: () => {
            onClose()
            postTaskCreated.mutate({
              task_type: values.category,
            })
          },
        }
      )
    },
  })

  function handleDiscardConfirmed() {
    setShowConfirm(false)
    onClose()
  }

  function handleClose() {
    if (form.dirty) {
      setShowConfirm(true)
    } else {
      onClose()
    }
  }

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      title={t(langKeys.task_management_create_a_new_task)}
      confirmLabel={t(langKeys.create_task)}
      formId={CREATE_TASK_FORM_ID}
      closeLabel={t(langKeys.cancel)}
      error={postTask.isError && t(langKeys.failed_general_error_title)}
      loading={postTask.isLoading}
    >
      {showConfirm && (
        <DiscardChangesDialog
          open={showConfirm}
          onClose={() => {
            setShowConfirm(false)
          }}
          onConfirm={handleDiscardConfirmed}
        />
      )}

      <form id={CREATE_TASK_FORM_ID} onSubmit={form.handleSubmit}>
        <TaskForm form={form} />
      </form>
    </Dialog>
  )
}
